import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, useNotifications } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { ArchivePartnerListDocument, PartnerListsDocument } from '../../graphql/__generated__/types';
import { partnerListRefusalOf } from './refusals';

interface Props {
    partnerListId: string;
    name: string;
    /** Read on the entry the screen already holds: see the header note. */
    holderCount: number;
    onClose: () => void;
}

/**
 * « Archiver la liste » (RG-PTN-08), and its refusal, in one component.
 *
 * **THE REFUSAL IS RENDERED BEFORE THE CALL, from a count the screen already holds**, and
 * that is the one place in this lot where the front pre-empts the server rather than
 * waiting for it. It is legitimate here for the reason the repo states: the refusal is not
 * a permission, it is a fact of the object displayed two lines above, so the operator can
 * see for themselves that the numbers agree. The server still refuses — the check here
 * never replaces it, and a stale count lands on `PARTNER_LIST_HAS_HOLDERS`, which is
 * rendered too.
 *
 * **The count travels through the screen and not through the error**, because `formatError`
 * on the API keeps only the code: no number could reach us in the refusal itself. That is
 * also why the sentence of the mockup — « 1 utilisateur est associé » — is written here.
 *
 * **Somebody who has left the company still counts.** They block the archiving until they
 * are detached, and the sentence must not suggest otherwise: an operator who reads « one
 * user » and sees a red « A quitté la société » badge would look for a second one.
 */
const ArchivePartnerListDialog = ({ partnerListId, name, holderCount, onClose }: Props) => {
    const { createNotification } = useNotifications();
    const navigate = useNavigate();
    const blocked = holderCount > 0;

    const [archive, { loading, error }] = useMutation(ArchivePartnerListDocument, {
        refetchQueries: [PartnerListsDocument],
        awaitRefetchQueries: true,
    });

    const submit = async () => {
        try {
            await archive({ variables: { partnerListId, reason: null } });
            createNotification({
                type: 'success',
                text: c('Partner lists').t`List « ${name} » archived`,
            });
            onClose();
            // The sheet of an archived list is still readable by its address, but the
            // operator did not ask to stay on it: they archived it.
            navigate('/listes-de-partenaires');
        } catch {
            // Rendered from `error` below.
        }
    };

    if (blocked) {
        return (
            <Modal open onClose={onClose} title={c('Partner lists').t`Archiving is not possible`} size={760}>
                <ModalContent>
                    <Alert type="warning">
                        <p className="font-semibold">
                            {c('Partner lists').ngettext(
                                msgid`You cannot archive the list « ${name} »: ${holderCount} user is attached to it.`,
                                `You cannot archive the list « ${name} »: ${holderCount} users are attached to it.`,
                                holderCount
                            )}
                        </p>
                        <p className="mt-1">
                            {c('Partner lists')
                                .t`Give them another list from their own user sheet, then archive this one. Somebody who has left the company counts here too, until they are detached.`}
                        </p>
                    </Alert>
                </ModalContent>
                <ModalFooter>
                    <Button onClick={onClose}>{c('Partner lists').t`Understood`}</Button>
                </ModalFooter>
            </Modal>
        );
    }

    return (
        <Modal open onClose={onClose} title={c('Partner lists').t`Archive this list?`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-3 text-sm">
                    <p>
                        {c('Partner lists')
                            .t`« ${name} » leaves the catalogue. A list is never deleted, and nothing brings an archived one back.`}
                    </p>
                    <p className="text-text-600">
                        {c('Partner lists')
                            .t`Its name stays reserved for ever, and no screen lists the archived ones: the theme cannot be used again. Its partners keep their PTN identifiers and stay in every other list they belong to.`}
                    </p>
                    {error && <RefusalNotice refusal={partnerListRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Partner lists').t`Cancel`}
                </Button>
                <Button category="destructive" onClick={submit} loading={loading}>
                    {c('Partner lists').t`Archive the list`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ArchivePartnerListDialog;
